import { ReduxUser } from 'app/redux/ReduxInterfaces'
import { OptionsObject, SnackbarMessage } from 'notistack'
import { ReactElement } from 'react'
import { History } from 'react-router-dom'

interface HandleErrorFeedbackParams {
  enqueueSnackbar: (message: SnackbarMessage, options?: OptionsObject) => void
  history?: History
  message: string | ReactElement
  redirectPath?: string
  timeout?: number
  error?: Error | unknown | null
  logError?: boolean
}

export function valueExistsTS(v): boolean {
  return Boolean(v !== null && v !== undefined && v)
}

export const handleErrorFeedback = ({
  enqueueSnackbar,
  history = null,
  message,
  redirectPath = '',
  timeout = 2500,
  error = null,
  logError = true
}: HandleErrorFeedbackParams): void => {
  if (logError && error != null) {
    console.error('Error:', error)
  }
  enqueueSnackbar(message, { variant: 'error' })
  if (history != null && redirectPath != null && redirectPath !== '') {
    setTimeout(
      () => {
        history?.push(redirectPath)
      },
      Math.max(0, timeout)
    )
  }
}

export const isValidSalesforceId = (
  id: string | null | undefined,
  allowUndefined: boolean = false // sometimes undefined means we're creating a new record, so it's allowed
): boolean => {
  if (id == null) {
    return allowUndefined
  }

  if (typeof id !== 'string') return false

  if (!/^[a-zA-Z0-9]{15}$|^[a-zA-Z0-9]{18}$/.test(id)) {
    return false
  }

  if (id.length === 15) {
    return true
  }

  if (id.length === 18) {
    return isValid18CharSalesforceId(id)
  }

  return false
}

// Helper function to validate 18-character Salesforce ID checksum
const isValid18CharSalesforceId = (id: string): boolean => {
  if (id.length !== 18) return false

  const upperCaseToBit = (char: string): string => (char.match(/[A-Z]/) != null ? '1' : '0')
  const binaryToSymbol = (digit: number): string =>
    digit <= 25 ? String.fromCharCode(digit + 65) : String.fromCharCode(digit - 26 + 48)

  const parts = [
    id.slice(0, 5).split('').reverse().map(upperCaseToBit).join(''),
    id.slice(5, 10).split('').reverse().map(upperCaseToBit).join(''),
    id.slice(10, 15).split('').reverse().map(upperCaseToBit).join('')
  ]

  const calculatedChecksum = parts.map((str) => binaryToSymbol(parseInt(str, 2))).join('')
  const actualChecksum = id.slice(-3)

  return calculatedChecksum === actualChecksum
}

export function truncate(str: string, max: number = 20, withEllipsis: boolean = true): string {
  if (str.length <= max) return str
  const truncated = str.slice(0, max)
  return withEllipsis ? `${truncated}…` : truncated
}

export function joinWithDifferentLastSeparator(
  arr: string[],
  separator: string,
  lastSeparator: string
): string {
  if (!Array.isArray(arr)) return ''
  if (arr.length === 0) return ''
  if (arr.length === 1) return arr[0]
  if (arr.length === 2) return arr[0] + lastSeparator + arr[1]
  return arr.slice(0, -1).join(separator) + lastSeparator + arr[arr.length - 1]
}

export const hasUserRole = (user: ReduxUser, role: string | string[]): boolean => {
  if (user.role == null) return false

  const rolesToCheck = Array.isArray(role) ? role : [role]
  const userRoles = Array.isArray(user.role) ? user.role : [user.role]

  return rolesToCheck.every((r) => userRoles.includes(r))
}

// Checks if:
// - the word contains "fake" or "test"
// - it is surrounded by non-letter characters or word boundaries
const TEST_CONTENT_NAME_REGEX =
  /(?:^|[^A-Za-z])(?:fake|test)(?:[^a-z]|$)|(?:Fake|Test)(?:[^a-z]|$)|(?:^|[^A-Z])(?:FAKE|TEST)(?:[^A-Za-z]|$|[A-Z][a-z])/

/**
 * Checks if a record name marks it as test content, hidden from users without the DEBUG or TESTER role.
 * @param name - Record name. Non-string values are never test content.
 * @returns True if the name contains "fake" or "test" as a separate word, written in lowercase, capitalized or uppercase.
 */
export const isTestContentName = (name: string): boolean => TEST_CONTENT_NAME_REGEX.test(name)

export const isVisibleForUser = (name: string | string[], user: ReduxUser): boolean => {
  const arr = Array.isArray(name) ? name : [name]

  return arr.every((name) => {
    if (isTestContentName(name)) {
      return isDebugOrTesterUser(user)
    }
    return true
  })
}

export const isDebugUser = (user: ReduxUser): boolean => {
  return hasUserRole(user, 'DEBUG')
}

export const isDebugOrTesterUser = (user: ReduxUser): boolean => {
  return hasUserRole(user, 'DEBUG') || hasUserRole(user, 'TESTER')
}

// Checks if a value is "falsy" without using the ! operator
export function isFalsy(value: unknown): boolean {
  return (
    value === false ||
    value === 0 ||
    value === '' ||
    value === null ||
    value === undefined ||
    Number.isNaN(value)
  )
}
